<template>
  <div id="headerModule">
    <!--  Header  -->
    <v-card
      :style="{
        padding: padding,
      }"
      elevation="3"
      class="d-flex justify-center align-center rounded-0"
      :class="{ 'pb-0': modelTabs?.length }"
      id="headerCard"
    >
      <v-row
        :style="useGridSystem ? '' : { width: '100%', maxWidth: width }"
        class="pa-0 ma-0"
        no-gutters
        :justify="useGridSystem ? 'center' : undefined"
      >
        <v-col
          :xl="useGridSystem ? 8 : undefined"
          :lg="useGridSystem ? 9 : undefined"
          :md="useGridSystem ? 11 : undefined"
          :sm="useGridSystem ? 12 : undefined"
        >
          <v-card-text class="pa-0 ma-0">
            <!-- Apply the width to max width only so we can handle small devices too -->
            <v-card-actions>
              <v-card-actions v-if="icon" class="mr-1">
                <v-icon size="23" :icon="`mdi-${icon}`" />
              </v-card-actions>
              <v-card-actions class="px-0">
                <div v-if="title" style="font-size: 24px; font-weight: 600">
                  {{ title }}
                </div>
                <component :is="dynamicChip"></component>
                <i class="pl-2" style="font-size: 14px">
                  {{ description }}
                </i>
              </v-card-actions>
            </v-card-actions>

            <!-- Tab headers -->
            <v-card-actions v-if="modelTabs?.length" class="pb-0">
              <v-tabs
                v-model="activeTab"
                color="primary"
                @update:modelValue="(newTab) => tabClickTransition(newTab)"
              >
                <v-tab
                  v-for="(tab, index) in modelTabs"
                  :key="index"
                  :value="tab.value"
                  :to="tab.route"
                  class="text-none"
                  :tabindex="0"
                  :data-testid="
                    tab.value === 'manage'
                      ? 'manage-themes-tab'
                      : 'search-indicators-tab'
                  "
                >
                  <div class="d-flex align-center">
                    <div style="font-weight: 500">{{ tab?.title }}</div>
                    <v-icon
                      v-if="tab.icon"
                      :icon="`mdi-${tab.icon}`"
                      class="ml-2"
                    />
                    <v-chip
                      v-if="tab.loading || tab.chip"
                      class="ml-2 d-flex align-center"
                      small
                      color="primary"
                      outlined
                    >
                      <template v-if="tab.loading">
                        <div
                          class="d-flex justify-center align-center"
                          style="width: 100%; height: 100%"
                        >
                          <v-progress-circular
                            indeterminate
                            color="primary"
                            size="14"
                          ></v-progress-circular>
                        </div>
                      </template>
                      <template v-else>
                        {{ tab.chip }}
                      </template>
                    </v-chip>
                  </div>
                </v-tab>
              </v-tabs>
            </v-card-actions>
          </v-card-text>
        </v-col>
      </v-row>
    </v-card>

    <!--  Content  -->
    <v-card
      v-if="modelTabs?.length || $slots.staticContent"
      class="d-flex justify-center mt-1 pa-0 w-100"
      elevation="0"
      style="background-color: #f9f9f9"
      :style="{
        overflowY: 'auto',
        height: `${contentHeight}px`,
        overflowX: 'hidden',
        scrollbarGutter: 'auto',
      }"
      @scroll="handleScroll($event)"
    >
      <v-card-text
        class="py-0 px-0"
        :style="{
          width: '100%',
          maxWidth: width,
        }"
      >
        <!-- Content of the tabs -->
        <v-tabs-window
          v-if="modelTabs?.length"
          v-model="activeTab"
          :style="{ height: screenHeight - 186 }"
        >
          <v-tabs-window-item
            v-for="(tab, index) in modelTabs"
            :key="index"
            :value="tab.value"
          >
            <slot :name="tab.value" />
          </v-tabs-window-item>
        </v-tabs-window>

        <!-- Static content when no tabs are needed but we need to align the content of the module with the width of the header -->
        <div v-else>
          <slot name="staticContent" />
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import { useDisplay } from "vuetify";
import { defineAsyncComponent } from "vue";

export default {
  name: "HeaderModule",
  props: {
    title: String,
    icon: String,
    description: String,
    chip: {
      type: String,
      default: null,
    },
    width: {
      type: String,
      default: "100%",
    },
    padding: {
      type: String,
      default: "10px",
    },
    // example: [ { title: "Catalogue", value: "catalogue", icon: "view-dashboard", link: "https://example.com" }, ]
    tabs: {
      type: Array,
      default: () => [],
    },
    selectedTabRoute: {
      type: String,
      default: null,
    },
    //
    defaultTab: {
      type: String,
      default: null,
      required: false,
    },
    useGridSystem: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  components: {
    //if chip prop string is passed through asynchronously import component of same name
    BetaChip: defineAsyncComponent(() => import("@/components/BetaChip")),
  },
  data: () => ({
    previousTab: null,
    screenHeight: useDisplay().height,
    dynamicChip: null,
  }),
  mounted() {
    // default tab
    if (this.defaultTab && this.tabs?.length) {
      this.activeTab = this.defaultTab;
    }

    this.dynamicChip = this.chip;
  },
  computed: {
    /**
     * Represents the currently active tab.
     * The property is synced with the URL
     */
    activeTab: {
      get() {
        return (
          this.modelTabs.find(
            (t) => t.route === (this.$route.params?.selectedTabRoute || ""),
          )?.value || ""
        );
      },
      set(val) {
        const selectedTabRoute =
          this.modelTabs.find((t) => t.value === val)?.route || val;

        // navigate to the new route and keep any queries
        this.$router.replace({
          params: { selectedTabRoute },
          query: { ...this.$route.query },
        });
      },
    },
    modelTabs: {
      get() {
        return this.tabs;
      },
      set(val) {
        this.$emit("update:tabs", val);
      },
    },
    // The content height is slightly greater when static content is displayed compared to tabs
    contentHeight() {
      return this.screenHeight - (this.modelTabs?.length ? 190 : 150);
    },
  },
  methods: {
    /** if @scrollTabEvent emit is passed then we can trigger this when user scrolls to the bottom of the tab.
     * This is useful for lazy loading content in tabs
     */
    handleScroll(event) {
      // if prop onScrollTabEvent is passed then trigger that up to the parent
      if (this.$attrs["onScrollTabEvent"]) {
        if (
          event.target.offsetHeight + event.target.scrollTop >=
          event.target.scrollHeight - 350
        ) {
          this.$emit("scrollTabEvent", this.activeTab);
        }
      }
    },
    /**
     * emits event when a header tab is clicked and then essentially uses a timer to mark
     * the end of the v-tab transition by waiting for a short period of time and checking
     * that the selected tab matches active tab.  Another event is emitted when this has
     * happened.  This is because there appear to be no transition events in vuetify, so this
     * function can be used to delay show hide actions when tabs are changing.
     */
    tabClickTransition(newTab) {
      this.$emit("headerTabClicked", newTab);

      let loop = 0; //safety net so we can bail if we get into infinite loop
      //loop at interval and check whether tabs have transitioned
      const transitionInterval = setInterval(() => {
        if (loop > 100 || newTab == this.activeTab) {
          this.$emit("headerTabTransitionEnd", newTab);
          clearInterval(transitionInterval);
        }
        loop++;
      }, 400);
    },
  },
  watch: {
    /** watch active tab changes and open links or update browser URL if requried */
    activeTab(newVal, oldVal) {
      const newTab = this.modelTabs.find((t) => t.value === newVal);

      // check if this is a link tab and redirect to the link
      if (newTab?.link) {
        window.open(newTab.link, "_blank");

        // go back to the previous tab
        this.activeTab = this.modelTabs.find((t) => t.value === oldVal).value;
        return;
      }
    },
  },
};
</script>

<style scoped>
* {
  color: #4d4d4d;
  font-family: "Inter", sans-serif;
}
</style>
